import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getExcursion, getSteps } from '../../api';
import { t, useLang } from '../../i18n';
import { Icon, LangSwitcher, LogoTile, RouteMap, googleDirectionsUrl, routeStats, useMyPosition } from './Kit';
import '../../styles/vod.css';
import './screens.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const BASE = API_URL.replace('/api', '');

export function routePoints(steps) {
  return (steps || [])
    .filter(s => s.lat && s.lng && !isNaN(parseFloat(s.lat)) && !isNaN(parseFloat(s.lng)))
    .sort((a, b) => a.step_number - b.step_number)
    .map(s => ({ lat: parseFloat(s.lat), lng: parseFloat(s.lng), title: s.title }));
}

export default function TourScreen() {
  const lang = useLang();
  const { excursionId } = useParams();
  const navigate = useNavigate();
  const [exc, setExc] = useState(null);
  const [steps, setSteps] = useState([]);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const { pos: myPos, status: locStatus } = useMyPosition(showMap);

  useEffect(() => {
    let alive = true;
    getExcursion(excursionId).then(e => alive && setExc(e)).catch(e => alive && setError(e.message));
    getSteps(excursionId).then(d => alive && setSteps(d.steps || [])).catch(() => {});
    return () => { alive = false; };
  }, [excursionId, lang]);

  if (error) return <div className="vod vod-loading">{error}</div>;
  if (!exc) return <div className="vod vod-loading">{t('loading')}</div>;

  const pts = routePoints(steps);
  const stats = pts.length > 1 ? routeStats(pts) : null;
  const started = exc.current_step > 1 && !exc.is_completed;
  const startStep = started ? exc.current_step : 1;
  const gallery = Array.isArray(exc.gallery) ? exc.gallery.filter(Boolean) : [];

  return (
    <main className="vod vod-screen">
      <div className="vod-hero" style={{ top: -20, height: '82%', backgroundImage: exc.image_url ? `url(${BASE + exc.image_url})` : undefined, backgroundPosition: 'center top' }} />
      <div className="vod-top" style={{ minHeight: 330, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 36px)' }}>
        <Link className="vod-round icon back" to="/excursions" aria-label="back">{Icon.back()}</Link>
        <LogoTile size="md" />
        <LangSwitcher />
      </div>

      <section className="vod-card">
        <div className="vod-accent" />
        <h1 className="vod-h1 vod-serif" style={{ fontSize: 38, lineHeight: '44px', letterSpacing: '-.02em' }} dir="auto">{exc.title}</h1>
        {exc.subtitle && <div className="vod-area" style={{ fontSize: 13, letterSpacing: '.24em' }} dir="auto">{exc.subtitle}</div>}
        {!exc.subtitle && <div style={{ height: 12 }} />}
        <div className="vod-meta">
          {exc.district && <><span>{Icon.pin()}<span dir="auto">{exc.district}</span></span><i /></>}
          <span>{Icon.headphones()}{exc.has_points ? t('quest') : t('audio_walk')}</span>
          {exc.duration_min > 0 && <><i /><span>{Icon.clock()}{t('min', { n: exc.duration_min })}</span></>}
        </div>
        {exc.description && <p className="vod-text" dir="auto" style={{ whiteSpace: 'pre-line' }}>{exc.description}</p>}

        {gallery.length > 0 && (
          <div className="vodt-gallery">
            {gallery.slice(0, 3).map((g, i) => <img key={i} src={BASE + g} alt="" onClick={() => setZoom(BASE + g)} />)}
          </div>
        )}

        {pts.length > 0 && (
          <div className="vodt-route">
            <div className="vodt-route-head">
              <b>{t('route')}</b>
              <div className="vodt-route-stats">
                <span>{Icon.pin({ size: 16 })}{t('stops', { n: pts.length })}</span>
                {stats && <span>{Icon.walk({ size: 16 })}{t('walk_km', { km: stats.km })}</span>}
                {stats && <span>{Icon.clock({ size: 16 })}{t('walk_time', { min: stats.min })}</span>}
              </div>
            </div>
            {showMap ? (<>
              <RouteMap points={myPos ? [myPos, ...pts] : pts} height={260} />
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--vod-muted)' }}>{locStatus === 'ok' ? t('you_here') : locStatus === 'asking' ? t('loc_asking') : locStatus === 'denied' ? t('loc_denied') : locStatus === 'unavailable' ? t('loc_unavailable') : ''}</div>
            </>) : (
              <button type="button" className="vod-btn2" style={{ marginTop: 0 }} onClick={() => setShowMap(true)}>{Icon.map()}{t('view_on_map')}</button>
            )}
            <a className="vodt-link" href={googleDirectionsUrl(pts)} target="_blank" rel="noreferrer">{Icon.map({ size: 16, color: '#B8893A' })}{t('open_in_google')}</a>
          </div>
        )}

        <button className="vod-btn caps" onClick={() => navigate(`/excursion/${excursionId}/step/${startStep}`)}>
          {started ? t('continue_tour') : t('start')}{Icon.arrow()}
        </button>
      </section>

      {zoom && (
        <div onClick={() => setZoom(null)} style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.9)', display: 'grid', placeItems: 'center', padding: 16 }}>
          <img src={zoom} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 12 }} />
        </div>
      )}
    </main>
  );
}
